import { Alert, Button, Container, Stack, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';
import { hasOfficeAccess, useAccessQuery, WorkplaceProvider } from './core/access';
import { useSession } from './core/auth';
import { Disclaimer, Layout } from './core/Layout';
import { ProfilePage } from './core/ProfilePage';
import { SignInPage } from './core/SignInPage';
import { useDb } from './core/supabase';
import { AdminCompaniesPage } from './core/admin/AdminCompaniesPage';
import { AdminContentPage } from './core/admin/AdminContentPage';
import { AdminOfficeUsersPage } from './core/admin/AdminOfficeUsersPage';
import { AdminVesselsPage } from './core/admin/AdminVesselsPage';
import { CrewPage } from './core/CrewPage';
import { ContentEditorPage } from './modules/risq/pages/ContentEditorPage';
import { FleetPage } from './modules/risq/pages/FleetPage';
import { MassReassignPage } from './modules/risq/pages/MassReassignPage';
import { QuestionPage } from './modules/risq/pages/QuestionPage';
import { VesselPage } from './modules/risq/pages/VesselPage';
import { VesselQuestionsPage } from './modules/risq/pages/VesselQuestionsPage';
import { ActivityPage } from './modules/risq/pages/ActivityPage';
import { WaitingPage } from './modules/risq/pages/WaitingPage';
import { WeeklyPage } from './modules/risq/pages/WeeklyPage';
import { Loading, ErrorNotice } from './ui/components';

function NoOfficeAccess() {
  const { t } = useTranslation('dashboard');
  const db = useDb();
  return (
    <Container size={520} py={60}>
      <Stack>
        <Title order={1}>{t('noAccess.title')}</Title>
        <Alert color="yellow">{t('noAccess.body')}</Alert>
        <Button variant="default" onClick={() => void db.auth.signOut()}>
          {t('profile.signOut')}
        </Button>
        <Disclaimer />
      </Stack>
    </Container>
  );
}

/** Signed in with office access → the dashboard; otherwise sign-in or "no access". */
function Gate() {
  const session = useSession();
  const access = useAccessQuery();
  if (session === undefined) {
    return <Loading />;
  }
  if (session === null) {
    return <SignInPage />;
  }
  if (access.error !== null) {
    return <ErrorNotice error={access.error} />;
  }
  if (access.data === undefined) {
    return <Loading />;
  }
  if (!hasOfficeAccess(access.data)) {
    return <NoOfficeAccess />;
  }
  return (
    <WorkplaceProvider access={access.data}>
      <Outlet />
    </WorkplaceProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <Gate />,
    children: [
      {
        element: <Layout />,
        children: [
          { index: true, element: <FleetPage /> },
          { path: 'waiting', element: <WaitingPage /> },
          { path: 'weekly', element: <WeeklyPage /> },
          { path: 'vessels/:vesselId', element: <VesselPage /> },
          { path: 'vessels/:vesselId/questions', element: <VesselQuestionsPage /> },
          { path: 'vessels/:vesselId/questions/:itemId', element: <QuestionPage /> },
          { path: 'vessels/:vesselId/crew', element: <CrewPage /> },
          { path: 'vessels/:vesselId/activity', element: <ActivityPage /> },
          { path: 'content', element: <ContentEditorPage /> },
          { path: 'content/:questionKey', element: <ContentEditorPage /> },
          { path: 'reassign', element: <MassReassignPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'admin/companies', element: <AdminCompaniesPage /> },
          { path: 'admin/vessels', element: <AdminVesselsPage /> },
          { path: 'admin/office-users', element: <AdminOfficeUsersPage /> },
          { path: 'admin/content', element: <AdminContentPage /> },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
